import React, { useState } from "react";
import { supabase } from "../../../supabaseClient";
import { useAdmin } from "../../../context/AdminContext";
import { InlineNotice } from "../../../utils/InlineNotice";
import { useInlineNotice } from "../../../hooks/useInlineNotice";
import { InlineConfirm } from "../../../utils/InlineConfirm";

const STAT_COLS = [
  { key: "points", label: "PTS" },
  { key: "three_pm", label: "3PM" },
  { key: "two_pm", label: "2PM" },
  { key: "ft_made", label: "FTM" },
  { key: "rebounds", label: "REB" },
  { key: "assists", label: "AST" },
  { key: "personal_fouls", label: "PF" },
  { key: "technical_fouls", label: "TF" },
];

const BLANK_STATS = {
  points: 0,
  three_pm: 0,
  two_pm: 0,
  ft_made: 0,
  rebounds: 0,
  assists: 0,
  personal_fouls: 0,
  technical_fouls: 0,
};

/**
 * CorrectGameModal
 *
 * Props:
 *   game          — finished game row (with team1/team2 joins)
 *   onClose       — () => void
 *   onSaved       — () => void   called after a successful save/delete
 */
const CorrectGameModal = ({ game, onClose, onSaved }) => {
  const { adminId } = useAdmin();

  const [scores, setScores] = useState({
    team1Score: game.team1_score,
    team2Score: game.team2_score,
  });
  const [stats, setStats] = useState({}); // { [playerId]: statObj }
  const [players, setPlayers] = useState(null); // null = not yet loaded
  const [tab, setTab] = useState("team1");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const { notice, showError, clear } = useInlineNotice();

  // ── Lazy-load players + existing stats on first open ─────────────────────
  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      let t1 = [];
      let t2 = [];
      let existingStats = [];

      if (game.game_type === "all_star") {
        // All-star squad membership lives in all_star_selections, not
        // players.team_id — a player's real team never changes just
        // because they made the all-star roster. Pull the roster from
        // there instead and split by squad.
        const { data: selections, error: selectionsError } = await supabase
          .from("all_star_selections")
          .select(
            "team_id, player:players!all_star_selections_player_id_fkey(id, name, jersey_number)",
          )
          .eq("game_id", game.id);

        if (selectionsError) {
          console.error("Failed to load all-star roster:", selectionsError);
        }

        const { data: statsData } = await supabase
          .from("game_stats")
          .select("*")
          .eq("game_id", game.id);

        (selections || []).forEach((s) => {
          if (!s.player) return;
          if (s.team_id === game.team1.id) t1.push(s.player);
          else if (s.team_id === game.team2.id) t2.push(s.player);
        });
        t1.sort((a, b) => (a.jersey_number ?? 99) - (b.jersey_number ?? 99));
        t2.sort((a, b) => (a.jersey_number ?? 99) - (b.jersey_number ?? 99));
        existingStats = statsData || [];
      } else {
        const [{ data: t1Data }, { data: t2Data }, { data: statsData }] =
          await Promise.all([
            supabase
              .from("players")
              .select("id, name, jersey_number")
              .eq("team_id", game.team1.id)
              .order("jersey_number", { ascending: true }),
            supabase
              .from("players")
              .select("id, name, jersey_number")
              .eq("team_id", game.team2.id)
              .order("jersey_number", { ascending: true }),
            supabase.from("game_stats").select("*").eq("game_id", game.id),
          ]);
        t1 = t1Data || [];
        t2 = t2Data || [];
        existingStats = statsData || [];
      }

      if (cancelled) return;

      const statMap = {};
      (existingStats || []).forEach((s) => {
        statMap[s.player_id] = {
          points: s.points ?? 0,
          three_pm: s.three_pm ?? 0,
          two_pm: s.two_pm ?? 0,
          ft_made: s.ft_made ?? 0,
          rebounds: s.rebounds ?? 0,
          assists: s.assists ?? 0,
          personal_fouls: s.personal_fouls ?? 0,
          technical_fouls: s.technical_fouls ?? 0,
        };
      });
      [...t1, ...t2].forEach((p) => {
        if (!statMap[p.id]) statMap[p.id] = { ...BLANK_STATS };
      });

      setPlayers({ team1: t1, team2: t2 });
      setStats(statMap);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [game]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const updateStat = (playerId, field, value) =>
    setStats((prev) => ({
      ...prev,
      [playerId]: { ...prev[playerId], [field]: value },
    }));

  const handleSave = async () => {
    clear();
    setSaving(true);
    try {
      const allPlayers = [...(players?.team1 ?? []), ...(players?.team2 ?? [])];
      const playerStats = allPlayers
        .filter((p) =>
          Object.values(stats[p.id] ?? {}).some((v) => Number(v) !== 0),
        )
        .map((p) => ({ player_id: p.id, ...stats[p.id] }));

      const { error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "correctGame",
          adminId,
          payload: {
            gameId: game.id,
            team1Score: Number(scores.team1Score),
            team2Score: Number(scores.team2Score),
            playerStats,
          },
        },
      });
      if (error) throw error;
      onSaved();
      onClose();
    } catch (err) {
      showError(`Error saving correction: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reason) => {
    if (game.game_type !== "regular") {
      showError("Only regular games can be deleted from here.");
      return;
    }
    clear();
    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "deleteFinishedGame",
          adminId,
          payload: { gameId: game.id, reason: reason || null },
        },
      });
      if (error) throw error;
      onSaved();
      onClose();
    } catch (err) {
      showError(`Error deleting game: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const t1Score = Number(scores.team1Score);
  const t2Score = Number(scores.team2Score);
  const winnerName =
    t1Score > t2Score
      ? game.team1?.name
      : t2Score > t1Score
        ? game.team2?.name
        : null;

  // Parse date without UTC shift
  const [datePart] = (game.game_date || "").split("T");
  const [y, m, d] = (datePart || "").split("-").map(Number);
  const localDate = new Date(y, m - 1, d);
  const displayDate = localDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  const currentPlayers =
    tab === "team1" ? (players?.team1 ?? []) : (players?.team2 ?? []);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center p-4 pt-12 z-[60] overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl w-full max-w-3xl overflow-hidden mb-12">
        {/* ── Header ── */}
        <div className="px-6 pt-6 pb-4 border-b dark:border-gray-700">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            Correct Game
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {game.team1?.name} vs {game.team2?.name} · {displayDate}
          </p>
        </div>

        <div className="p-6 space-y-8">
          <InlineNotice notice={notice} onDismiss={clear} />

          {/* ── Score ── */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Final Score
            </h4>
            <div className="flex items-center gap-4">
              {/* Team 1 */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 truncate">
                  {game.team1?.name}
                </label>
                <input
                  type="number"
                  min="0"
                  value={scores.team1Score}
                  onChange={(e) =>
                    setScores((prev) => ({
                      ...prev,
                      team1Score: e.target.value,
                    }))
                  }
                  className="w-full border-2 dark:border-gray-600 rounded-xl px-4 py-3 text-2xl font-black
                                               tabular-nums text-center text-orange-500 dark:text-orange-400
                                               bg-white dark:bg-gray-700 focus:border-orange-500 focus:outline-none"
                />
              </div>

              <span className="text-2xl font-black text-gray-400 mt-5">–</span>

              {/* Team 2 */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 truncate">
                  {game.team2?.name}
                </label>
                <input
                  type="number"
                  min="0"
                  value={scores.team2Score}
                  onChange={(e) =>
                    setScores((prev) => ({
                      ...prev,
                      team2Score: e.target.value,
                    }))
                  }
                  className="w-full border-2 dark:border-gray-600 rounded-xl px-4 py-3 text-2xl font-black
                                               tabular-nums text-center text-sky-500 dark:text-sky-400
                                               bg-white dark:bg-gray-700 focus:border-sky-500 focus:outline-none"
                />
              </div>
            </div>

            {winnerName && (
              <p className="text-xs text-center mt-2 text-gray-500 dark:text-gray-400">
                Winner:{" "}
                <span className="font-bold text-emerald-600 dark:text-emerald-400">
                  {winnerName}
                </span>
                {game.game_type === "regular" && (
                  <span className="ml-1 text-gray-400">
                    (W/L records will recalculate)
                  </span>
                )}
              </p>
            )}
          </div>

          {/* ── Player Stats ── */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Player Stats
              <span className="ml-2 normal-case font-normal text-gray-400">
                — leave at 0 to skip
              </span>
            </h4>

            {/* Team tabs */}
            <div className="flex border-b dark:border-gray-700 mb-4">
              {[
                { key: "team1", label: game.team1?.name },
                { key: "team2", label: game.team2?.name },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                    tab === t.key
                      ? "border-orange-500 text-orange-600 dark:text-orange-400"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {loading ? (
              <p className="text-sm text-gray-400 text-center py-6">
                Loading players…
              </p>
            ) : currentPlayers.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">
                No players on this team's roster.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b dark:border-gray-700">
                      <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 dark:text-gray-400">
                        Player
                      </th>
                      {STAT_COLS.map((col) => (
                        <th
                          key={col.key}
                          className="w-14 text-center py-2 px-1 text-xs font-semibold text-gray-500 dark:text-gray-400"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentPlayers.map((player) => {
                      const s = stats[player.id] || {};
                      return (
                        <tr
                          key={player.id}
                          className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                        >
                          <td className="py-2 pr-3 whitespace-nowrap">
                            <span className="font-bold text-orange-500 dark:text-orange-400 mr-1.5 tabular-nums">
                              #{player.jersey_number ?? "–"}
                            </span>
                            <span className="text-gray-800 dark:text-gray-200 text-xs">
                              {player.name}
                            </span>
                          </td>
                          {STAT_COLS.map((col) => (
                            <td key={col.key} className="w-14 px-1 py-1.5">
                              <input
                                type="number"
                                min="0"
                                value={s[col.key] ?? 0}
                                onChange={(e) =>
                                  updateStat(player.id, col.key, e.target.value)
                                }
                                className="w-full text-center text-sm tabular-nums rounded-lg border
                                                                           dark:border-gray-600 px-1 py-1.5
                                                                           bg-white dark:bg-gray-700
                                                                           text-gray-900 dark:text-white
                                                                           focus:border-orange-500 focus:outline-none
                                                                           [appearance:textfield]
                                                                           [&::-webkit-outer-spin-button]:appearance-none
                                                                           [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t dark:border-gray-700 p-4 space-y-3">
          {game.game_type === "regular" && (
            <InlineConfirm
              triggerLabel="🗑 Delete Game"
              triggerClassName="w-full py-2.5 text-sm font-semibold text-red-600 dark:text-red-400
                                       border border-red-300 dark:border-red-800 rounded-2xl
                                       hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition"
              message={`Delete this game permanently?\n\n${game.team1?.name} ${game.team1_score} – ${game.team2_score} ${game.team2?.name}\n\n• Game stats will be backed up\n• W/L records will recalculate\n• Cannot be undone from UI`}
              confirmLabel="Delete Game"
              requireReason
              reasonPlaceholder="Reason for deletion (saved to audit log, optional)"
              onConfirm={handleDelete}
            />
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-3 text-gray-700 dark:text-gray-300 font-medium
                                       border dark:border-gray-600 rounded-2xl
                                       hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="flex-1 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400
                                       text-white font-semibold rounded-2xl transition active:scale-95"
            >
              {saving ? "Saving…" : "Save Correction"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorrectGameModal;
